// NOTICE: KAPTION-INTEGRATION
// Pure helpers for the reply-box "Plantilla" button (KAPTION_REPLYBOX_TEMPLATE_PICKER).
// Spec: features/kaption/reply-template-picker/reply-template-picker.feature.yaml
//
// The payloads reuse the new-conversation wizard's helpers unchanged, so a
// template sent from the reply box reaches the backend in exactly the shape the
// wizard already uses (template_params with processed_params body/header/buttons).
import {
  buildTemplateParams,
  buildScheduledMessagePayload,
  renderTemplateBody,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';

export const REPLY_TEMPLATE_FLAG = 'KAPTION_REPLYBOX_TEMPLATE_PICKER';

// Only Channel::Whatsapp inboxes (Cloud API and 360dialog) carry Meta
// message_templates; Twilio WhatsApp uses Content templates, which this picker
// does not list.
const WHATSAPP_CHANNEL = 'Channel::Whatsapp';

const TEMPLATE_MODES = [
  REPLY_EDITOR_MODES.REPLY,
  REPLY_EDITOR_MODES.FOLLOWUP,
  REPLY_EDITOR_MODES.SCHEDULE,
];

/** reply-template-picker.BUTTON.2 — a template that Meta has not approved yet. */
export const isTemplatePending = template =>
  Boolean(template) &&
  String(template.status || '').toUpperCase() !== 'APPROVED';

/** reply-template-picker.BUTTON.1 — whether the inbox can list Meta templates. */
export const isTemplateCapableInbox = inbox =>
  inbox?.channel_type === WHATSAPP_CHANNEL;

/**
 * reply-template-picker.BUTTON.1 — the button shows for a WhatsApp (Meta)
 * inbox, in Responder / Seguimiento / Programar, never in a private note or
 * while editing a message, and only when the agent may use templates and the
 * box is not restricted (closed window with no template sending available).
 */
export const shouldShowReplyTemplateButton = ({
  flagEnabled,
  canUseTemplates,
  inbox,
  mode,
  isEditingMessage = false,
  isReplyRestricted = false,
}) =>
  Boolean(
    flagEnabled &&
      canUseTemplates &&
      isTemplateCapableInbox(inbox) &&
      !isEditingMessage &&
      !isReplyRestricted &&
      TEMPLATE_MODES.includes(mode)
  );

/**
 * reply-template-picker.SEND.1 — the message payload handed to ReplyBox's
 * `sendMessage`, the same function the Seguimiento send uses.
 */
export const buildPickedTemplateMessagePayload = ({
  conversationId,
  template,
  bodyValues = {},
  headerMedia = null,
  buttonValues = {},
  headerValues = {},
  sender,
}) => ({
  conversationId,
  message: renderTemplateBody(template, bodyValues),
  private: false,
  sender,
  templateParams: buildTemplateParams(
    template,
    bodyValues,
    headerMedia,
    buttonValues,
    headerValues
  ),
});

/**
 * reply-template-picker.SCHEDULE.1 / SEND.2 — the scheduled-message payload,
 * identical to the wizard's `scheduleLater`. A pending template is always
 * approval-gated (#335): the job releases it once Meta approves it.
 */
export const buildPickedTemplateSchedulePayload = ({
  template,
  bodyValues = {},
  headerMedia = null,
  buttonValues = {},
  headerValues = {},
  scheduledAt,
  timezone,
}) => ({
  ...buildScheduledMessagePayload({
    template,
    bodyValues,
    scheduledAt,
    timezone,
  }),
  message_content: renderTemplateBody(template, bodyValues),
  template_language: template?.language,
  template_params: buildTemplateParams(
    template,
    bodyValues,
    headerMedia,
    buttonValues,
    headerValues
  ),
  approval_required: isTemplatePending(template),
  // reply-template-picker.TYPES.5 — an uploaded header file (kapt.io, 7 days)
  // is kept alive until the scheduled send.
  ...(headerMedia?.attachment_hash
    ? { attachment_hash: headerMedia.attachment_hash }
    : {}),
});

/**
 * reply-template-picker.TYPES.6 — the inbox's approved and pending templates.
 * Unlike the upstream `inboxes/getDirectMessageTemplates` getter, shapes the
 * agent cannot send (location headers, catalog/product components) stay in
 * the list so the gallery can show them disabled with the reason instead of
 * hiding them silently.
 */
export const listPickerTemplates = inbox =>
  (Array.isArray(inbox?.message_templates)
    ? inbox.message_templates
    : []
  ).filter(
    template =>
      template?.components &&
      ['APPROVED', 'PENDING'].includes(
        String(template.status || '').toUpperCase()
      )
  );

/** Category badge text used by the chip: UTILITY → UTI, MARKETING → MAR. */
export const templateCategoryBadge = template =>
  String(template?.category || 'UTILITY')
    .slice(0, 3)
    .toUpperCase();

/**
 * Split a template body into text and variable parts, so the editor can render
 * the fixed text with an inline field for each `{{var}}`.
 * Returns `[{ type: 'text', text }, { type: 'var', key, token }]`.
 */
export const splitTemplateBody = text => {
  const parts = [];
  const source = String(text || '');
  const re = /{{\s*([^}]+?)\s*}}/g;
  let last = 0;
  let match = re.exec(source);
  while (match) {
    if (match.index > last) {
      parts.push({ type: 'text', text: source.slice(last, match.index) });
    }
    parts.push({ type: 'var', key: match[1].trim(), token: match[0] });
    last = match.index + match[0].length;
    match = re.exec(source);
  }
  if (last < source.length) {
    parts.push({ type: 'text', text: source.slice(last) });
  }
  return parts;
};

/** Meta rejects template parameters with line breaks or tabs; flatten them. */
export const sanitizeTemplateValue = value =>
  String(value ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/ {4,}/g, '   ');
